import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { useCategoryStore } from '../stores/categoryStore';
import { t } from '../stores/languageStore';
import { postApi, uploadApi } from '../utils/api';
import { File as FileType } from '../types';
import toast from 'react-hot-toast';
import { Upload, X, Paperclip } from 'lucide-react';

const CreatePost: React.FC = () => {
  const { isAuthenticated, user } = useAuthStore();
  const { categories, fetchCategories } = useCategoryStore();
  const navigate = useNavigate();

  const [title, setTitle] = React.useState('');
  const [content, setContent] = React.useState('');
  const [categoryId, setCategoryId] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const [attachments, setAttachments] = React.useState<any[]>([]);
  const [uploadingAttachment, setUploadingAttachment] = React.useState(false);

  React.useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchCategories();
  }, [isAuthenticated, navigate, fetchCategories]);

  const handleUploadImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploading(true);
      const response = await uploadApi.uploadImage(file);
      if (response.success && response.data) {
        const imageUrl = response.data.url;
        const markdown = `![${file.name}](${imageUrl})`;
        setContent((prev) => prev + '\n' + markdown);
        toast.success(t('createPost.imageUploadSuccess'));
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('createPost.uploadFailed'));
    } finally {
      setUploading(false);
    }
  };

  const handleUploadAttachment = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // 检查文件大小（100MB）
    if (file.size > 100 * 1024 * 1024) {
      toast.error(t('createPost.fileTooLarge'));
      return;
    }

    try {
      setUploadingAttachment(true);
      const response = await uploadApi.uploadAttachment(file);
      if (response.success && response.data) {
        setAttachments((prev) => [...prev, response.data]);
        toast.success(t('createPost.attachmentUploadSuccess'));
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('createPost.uploadFailed'));
    } finally {
      setUploadingAttachment(false);
    }
  };

  const handleRemoveAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim()) {
      toast.error(t('createPost.enterTitle'));
      return;
    }
    if (!content.trim()) {
      toast.error(t('createPost.enterContent'));
      return;
    }
    if (!categoryId) {
      toast.error(t('createPost.selectCategory'));
      return;
    }

    try {
      setLoading(true);
      const response = await postApi.createPost({
        title,
        content,
        category_id: parseInt(categoryId),
        attachment_ids: attachments.map((a) => a.id),
      });
      if (response.success && response.data) {
        toast.success(t('createPost.postSuccess'));
        navigate(`/post/${response.data.postId}`);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('createPost.postFailed'));
    } finally {
      setLoading(false);
    }
  };

  // 获取所有版块（扁平化）
  const flattenCategories = (cats: any[]): any[] => {
    return cats.reduce((acc, cat) => {
      acc.push(cat);
      if (cat.children && cat.children.length > 0) {
        acc.push(...flattenCategories(cat.children));
      }
      return acc;
    }, []);
  };

  const allCategories = flattenCategories(categories);

  return (
    <div className="max-w-4xl mx-auto">
      <div className="card p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">{t('createPost.title')}</h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* 版块选择 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('createPost.selectBoard')} <span className="text-red-500">*</span>
            </label>
            <select
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              className="input"
            >
              <option value="">{t('createPost.pleaseSelectBoard')}</option>
              {allCategories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.parent_id ? `  └ ${cat.name}` : cat.name}
                </option>
              ))}
            </select>
          </div>

          {/* 标题 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('createPost.postTitle')} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('createPost.pleaseEnterTitle')}
              className="input"
              maxLength={100}
            />
          </div>

          {/* 内容 */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-gray-700">
                {t('createPost.content')} <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleUploadImage}
                    className="hidden"
                    disabled={uploading}
                  />
                  <span className="btn btn-secondary text-sm inline-flex items-center">
                    <Upload className="w-4 h-4 mr-1" />
                    {uploading ? t('createPost.uploading') : t('createPost.uploadImage')}
                  </span>
                </label>
                <label className="cursor-pointer">
                  <input
                    type="file"
                    onChange={handleUploadAttachment}
                    className="hidden"
                    disabled={uploadingAttachment}
                  />
                  <span className="btn btn-secondary text-sm inline-flex items-center">
                    <Paperclip className="w-4 h-4 mr-1" />
                    {uploadingAttachment ? t('createPost.uploading') : t('createPost.uploadAttachment')}
                  </span>
                </label>
              </div>
            </div>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder={t('createPost.pleaseEnterContent')}
              className="input min-h-[400px] resize-none font-mono"
            />
            <p className="text-xs text-gray-500 mt-1">
              {t('createPost.markdownTip')}
            </p>
          </div>

          {/* 附件列表 */}
          {attachments.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('createPost.attachedFiles')}
              </label>
              <div className="space-y-2">
                {attachments.map((attachment, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">
                        {attachment.is_image ? '🖼️' : '📁'}
                      </span>
                      <div>
                        <p className="font-medium text-gray-800">{attachment.filename}</p>
                        <p className="text-sm text-gray-500">
                          {(attachment.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleRemoveAttachment(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 提交按钮 */}
          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="btn btn-secondary"
            >
              {t('createPost.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary"
            >
              {loading ? t('createPost.publishing') : t('createPost.publishPost')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreatePost;
